import MapboxDraw, { DrawCustomModeThis } from "@mapbox/mapbox-gl-draw";
import { Feature } from "geojson";
import { delay } from "lodash";
import mapboxgl from "mapbox-gl";

import { DRAWING_CONSTANTS } from "fond/constants";
import { GenericValidator, ValidationFunction } from "fond/draw/validation/GenericValidator";

export interface ValidationState {
  validator?: GenericValidator;
  affectedFeatureIds?: Set<string>;
  validationFunctions?: Map<string, ValidationFunction>;
  map?: mapboxgl.Map;
}

/**
 * Validate a set of feature IDs and update their properties.
 * Shared helper for post-operation validation.
 *
 * @param featureIds - Set of feature IDs to validate
 */
function validateFeaturesByIds(this: DrawCustomModeThis, state: ValidationState, featureIds: Set<string>): void {
  if (!state.validator || !state.validationFunctions || featureIds.size === 0) return;

  // Get all features and build a map for O(1) lookups
  const allFeatures = this._ctx.api.getAll().features as Feature[];
  const featureMap = new Map<string, Feature>();
  for (const feature of allFeatures) {
    if (feature.id) {
      featureMap.set(feature.id as string, feature);
    }
  }

  // Validate each feature
  for (const featureId of featureIds) {
    const feature = featureMap.get(featureId);
    if (!feature) continue; // eslint-disable-line no-continue

    const layerId = feature.properties?.layerId;
    if (!layerId || !state.validationFunctions.has(layerId)) continue; // eslint-disable-line no-continue

    const validationFn = state.validationFunctions.get(layerId)!;
    const result = state.validator.validate(feature, validationFn);

    // Update feature properties
    this._ctx.api.setFeatureProperty(featureId, DRAWING_CONSTANTS.Valid, result.valid.toString());
    this._ctx.api.setFeatureProperty(featureId, DRAWING_CONSTANTS.ValidationErrors, result.valid ? "" : result.errors.join("; "));

    // Force re-render to update styles
    this.doRender(featureId);
  }
}

/**
 * Initialize validation for a draw mode.
 * Call this in onSetup.
 */
export function initializeValidation(this: DrawCustomModeThis, state: ValidationState): void {
  state.affectedFeatureIds = new Set();

  // Initialize validator if any layers have validation functions
  const allLayers = this._ctx.options.editOptions?.allLayers || [];
  const validationFunctions = new Map<string, ValidationFunction>();

  allLayers.forEach((layer) => {
    if (layer.validate) {
      validationFunctions.set(layer.ID, layer.validate);
    }
  });

  if (validationFunctions.size > 0) {
    const allFeatures = this._ctx.api.getAll().features;
    state.validator = new GenericValidator(allFeatures);
    state.validationFunctions = validationFunctions;
  }
}

/**
 * Capture features that need validation before a move operation.
 * Call this in onMouseDown after determining which features will be moved.
 *
 * @param featuresToMove - Features that will be moved (e.g., selected features)
 */
export function captureBeforeMoveFeatures(this: DrawCustomModeThis, state: ValidationState, featuresToMove: Feature[]): void {
  if (!state.validator || !state.validationFunctions) return;

  // Update validator with latest feature positions FIRST
  // This ensures the spatial index is populated with current features
  const allFeatures = this._ctx.api.getAll().features as Feature[];
  state.validator.setFeatures(allFeatures);

  const affectedFeatureIds = new Set<string>();

  // For each feature being moved, find all colocated features
  for (const f of featuresToMove) {
    if (f.id) {
      affectedFeatureIds.add(f.id as string);

      // Find all colocated features using the validator
      const colocated = state.validator.findFeaturesAtFeatureVertices(f);
      for (const cf of colocated) {
        if (cf.id) affectedFeatureIds.add(cf.id as string);
      }
    }
  }

  state.affectedFeatureIds = affectedFeatureIds;
}

/**
 * Validate features after a move operation.
 * Call this in onMouseUp after features have been moved.
 *
 * @param movedFeatures - Features that were moved
 */
export function validateAfterMove(this: DrawCustomModeThis, state: ValidationState, movedFeatures: Feature[]): void {
  if (!state.validator || !state.validationFunctions || !state.affectedFeatureIds) return;

  // Start with features captured before move
  const allFeatureIds = new Set<string>(state.affectedFeatureIds);

  // Update validator with latest feature positions
  const allFeatures = this._ctx.api.getAll().features as Feature[];
  state.validator.setFeatures(allFeatures);

  // For each moved feature, add it and its new colocated features to the validation set
  for (const feature of movedFeatures) {
    if (feature.id) {
      allFeatureIds.add(feature.id as string);

      // Find colocated features at new position
      const colocated = state.validator.findFeaturesAtFeatureVertices(feature);
      for (const cf of colocated) {
        if (cf.id) allFeatureIds.add(cf.id as string);
      }
    }
  }

  // Validate all affected features
  validateFeaturesByIds.call(this, state, allFeatureIds);

  // Fire custom event asynchronously to allow history update to complete first
  if (state.map && allFeatureIds.size > 0) {
    delay(() => {
      state.map?.fire("draw.validation", { type: "draw.validation" });
    }, 0);
  }

  // Clear the tracking
  state.affectedFeatureIds = new Set();
}

/**
 * Capture features that need validation before a delete operation.
 * Call this in onTrash before deleting features.
 *
 * @param featuresToDelete - Features that will be deleted (e.g., selected features)
 */
export function captureBeforeDeleteFeatures(this: DrawCustomModeThis, state: ValidationState, featuresToDelete: Feature[]): void {
  if (!state.validator || !state.validationFunctions || featuresToDelete.length === 0) return;

  // Update validator with latest features
  const allFeatures = this._ctx.api.getAll().features as Feature[];
  state.validator.setFeatures(allFeatures);

  const affectedFeatureIds = new Set<string>();

  // For each feature being deleted, find colocated features
  for (const deletedFeature of featuresToDelete) {
    const colocated = state.validator.findFeaturesAtFeatureVertices(deletedFeature);
    for (const cf of colocated) {
      if (cf.id && cf.id !== deletedFeature.id) {
        affectedFeatureIds.add(cf.id as string);
      }
    }
  }

  // Store affected feature IDs for validation after deletion
  state.affectedFeatureIds = affectedFeatureIds;
}

/**
 * Validate features after a delete operation.
 * Call this in onTrash after features have been deleted.
 */
export function validateAfterDelete(this: DrawCustomModeThis, state: ValidationState): void {
  if (!state.validator || !state.validationFunctions || !state.affectedFeatureIds || state.affectedFeatureIds.size === 0) return;

  // Update validator with features after deletion
  const allFeatures = this._ctx.api.getAll().features as Feature[];
  state.validator.setFeatures(allFeatures);

  // Validate all affected features (captured before deletion)
  validateFeaturesByIds.call(this, state, state.affectedFeatureIds);

  // Fire custom event asynchronously to allow history update to complete first
  if (state.map && state.affectedFeatureIds.size > 0) {
    delay(() => {
      state.map?.fire("draw.validation", { type: "draw.validation" });
    }, 0);
  }

  // Clear the tracking
  state.affectedFeatureIds = new Set();
}

/**
 * Helper function to validate newly created features and their colocated features.
 *
 * This function:
 * 1. Initializes the validator if needed
 * 2. Validates each newly created feature
 * 3. Also validates any features colocated with the newly created features
 *
 * @param drawControl - The MapboxDraw instance
 * @param createdFeatureIds - Array of feature IDs that were just created
 * @param allLayers - Array of editable layer configurations
 * @param validatorRef - Ref object containing the validator instance and validation functions
 * @param map - Optional map instance to fire validation event
 */
export function validateCreatedFeatures(
  drawControl: MapboxDraw,
  createdFeatureIds: string[],
  allLayers: Array<{ ID: string; validate?: ValidationFunction }> | undefined,
  validatorRef: React.MutableRefObject<{
    validator: GenericValidator;
    validationFunctions: Map<string, ValidationFunction>;
  } | null>,
  map?: mapboxgl.Map
): void {
  if (!drawControl) return;

  // Initialize validator if needed
  if (!validatorRef.current) {
    const validationFunctions = new Map<string, ValidationFunction>();
    allLayers?.forEach((layer) => {
      if (layer.validate) {
        validationFunctions.set(layer.ID, layer.validate);
      }
    });

    if (validationFunctions.size > 0) {
      const allFeatures = drawControl.getAll().features as Feature[];
      validatorRef.current = {
        validator: new GenericValidator(allFeatures),
        validationFunctions,
      };
    }
  }

  if (!validatorRef.current) return;

  const { validator, validationFunctions } = validatorRef.current;

  // Update validator with latest features
  const allFeatures = drawControl.getAll().features as Feature[];
  validator.setFeatures(allFeatures);

  // Fetch fresh features from draw control (after properties have been set)
  const featuresToValidate = allFeatures.filter((f) => f.id && createdFeatureIds.includes(f.id as string));

  // Validate each newly created feature
  for (const feature of featuresToValidate) {
    const featureId = feature.id as string;
    const layerId = feature.properties?.layerId;

    if (layerId && validationFunctions.has(layerId)) {
      const validationFn = validationFunctions.get(layerId)!;
      const result = validator.validate(feature, validationFn);

      // Update feature properties with validation result
      drawControl.setFeatureProperty(featureId, DRAWING_CONSTANTS.Valid, result.valid.toString());

      if (!result.valid) {
        drawControl.setFeatureProperty(featureId, DRAWING_CONSTANTS.ValidationErrors, result.errors.join("; "));
      } else {
        drawControl.setFeatureProperty(featureId, DRAWING_CONSTANTS.ValidationErrors, "");
      }

      // Also validate colocated features
      const colocatedFeatures = validator.findFeaturesAtFeatureVertices(feature);
      for (const colocated of colocatedFeatures) {
        if (colocated.id) {
          const colocatedLayerId = colocated.properties?.layerId;
          if (colocatedLayerId && validationFunctions.has(colocatedLayerId)) {
            const colocatedValidationFn = validationFunctions.get(colocatedLayerId)!;
            const colocatedResult = validator.validate(colocated, colocatedValidationFn);

            drawControl.setFeatureProperty(colocated.id as string, DRAWING_CONSTANTS.Valid, colocatedResult.valid.toString());

            if (!colocatedResult.valid) {
              drawControl.setFeatureProperty(colocated.id as string, DRAWING_CONSTANTS.ValidationErrors, colocatedResult.errors.join("; "));
            } else {
              drawControl.setFeatureProperty(colocated.id as string, DRAWING_CONSTANTS.ValidationErrors, "");
            }
          }
        }
      }
    }
  }

  if (createdFeatureIds.length > 0) {
    // setFeatureProperty doesn't trigger a re-render, so we add an empty collection to force one.
    drawControl.add({ type: "FeatureCollection", features: [] });

    // Fire custom event to notify that validation is complete
    if (map) {
      map.fire("draw.validation", { type: "draw.validation" });
    }
  }
}
